<template>
    <div>
        <NavbarOne/>

        <div class="flex items-center gap-4 flex-wrap bg-overlay p-14 sm:p-16 before:bg-title before:bg-opacity-70" :style="{backgroundImage:'url(' + bg + ')'}">
            <div class="text-center w-full">
                <h2 class="text-white text-8 md:text-[40px] font-normal leading-none text-center">Checkout</h2>
                <ul class="flex items-center justify-center gap-[10px] text-base md:text-lg leading-none font-normal text-white mt-3 md:mt-4 flex-wrap">
                    <li><router-link to="/index-v2">Home</router-link></li>
                    <li>/</li>
                    <li class="text-primary">Checkout</li>
                </ul>
            </div>
        </div>

        <div class="s-py-100">
            <div class="container">
                <div v-if="!items.length" class="max-w-[860px] mx-auto text-center bg-[#FAFAFA] dark:bg-dark-secondary p-10 md:p-16 rounded-xl border border-[#17243026] border-opacity-15">
                    <h3 class="font-semibold leading-none">There is nothing to check out yet</h3>
                    <p class="mt-4 text-base md:text-lg text-paragraph dark:text-white-light">Add a product with its modifier selections first, then we can carry it into the order.</p>
                    <div class="mt-8">
                        <router-link to="/shop-v2" class="btn btn-solid" data-text="Browse Products">
                            <span>Browse Products</span>
                        </router-link>
                    </div>
                </div>

                <div v-else class="max-w-[1220px] mx-auto grid lg:grid-cols-2 gap-[30px] lg:gap-[70px]">
                    <form class="bg-[#FAFAFA] dark:bg-dark-secondary p-[30px] md:p-[40px] lg:p-[50px] border border-[#17243026] border-opacity-15 rounded-xl" data-aos="fade-up" @submit.prevent="placeOrder">
                        <div class="flex items-center justify-between gap-4 flex-wrap mb-6 md:mb-[30px]">
                            <h4 class="font-semibold leading-none text-xl md:text-2xl">
                                Billing Information
                            </h4>
                            <span class="text-sm text-paragraph dark:text-white-light">{{ isAuthenticated ? 'Auto-filled from your account' : 'Guest checkout' }}</span>
                        </div>

                        <div v-if="isAuthenticated" class="mb-6 md:mb-8 grid gap-4">
                            <div v-if="savedAddresses.length" class="rounded-xl border border-[#17243026] border-opacity-15 bg-white dark:bg-title/20 p-4 md:p-5">
                                <div class="flex items-center justify-between gap-3 flex-wrap mb-4">
                                    <h5 class="font-medium leading-none">Saved addresses</h5>
                                    <button
                                        v-if="selectedAddressId"
                                        type="button"
                                        class="text-sm text-primary underline underline-offset-4"
                                        @click="clearSelectedAddress"
                                    >
                                        Use current form instead
                                    </button>
                                </div>
                                <div class="grid gap-3">
                                    <button
                                        v-for="address in savedAddresses"
                                        :key="address.id"
                                        type="button"
                                        class="text-left rounded-xl border p-4 duration-300"
                                        :class="selectedAddressId === address.id
                                            ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                            : 'border-[#17243026] border-opacity-15 hover:border-primary/40 bg-[#FAFAFA] dark:bg-dark-secondary'"
                                        @click="selectSavedAddress(address)"
                                    >
                                        <div class="flex items-center justify-between gap-3">
                                            <div>
                                                <div class="font-medium text-title dark:text-white">{{ address.full_name }}</div>
                                                <div class="mt-1 text-sm text-paragraph dark:text-white-light">{{ address.full_address }}</div>
                                                <div class="mt-1 text-sm text-paragraph dark:text-white-light">{{ address.phone }}</div>
                                            </div>
                                            <span
                                                v-if="address.is_default"
                                                class="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                                            >
                                                Default
                                            </span>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            <label class="flex items-center gap-3">
                                <input
                                    v-model="saveAddressForNextTime"
                                    type="checkbox"
                                    class="h-4 w-4 accent-primary"
                                >
                                <span class="text-sm md:text-base text-title dark:text-white">
                                    Save this address for next time
                                </span>
                            </label>
                        </div>

                        <div class="grid gap-5 md:gap-6">
                            <div>
                                <label class="text-base md:text-lg text-title dark:text-white leading-none mb-2 sm:mb-3 block">Full Name</label>
                                <input v-model="form.contact_name" class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300" type="text" placeholder="Enter your full name">
                                <p v-if="fieldError('contact_name')" class="mt-2 text-sm text-[#E13939]">{{ fieldError('contact_name') }}</p>
                            </div>

                            <div>
                                <label class="text-base md:text-lg text-title dark:text-white leading-none mb-2 sm:mb-3 block">Email</label>
                                <input v-model="form.contact_email" class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300" type="email" placeholder="Enter your email address">
                                <p v-if="fieldError('contact_email')" class="mt-2 text-sm text-[#E13939]">{{ fieldError('contact_email') }}</p>
                            </div>

                            <div>
                                <label class="text-base md:text-lg text-title dark:text-white leading-none mb-2 sm:mb-3 block">Phone No.</label>
                                <input v-model="form.contact_phone" class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300" type="text" placeholder="Type your phone number">
                                <p v-if="fieldError('contact_phone')" class="mt-2 text-sm text-[#E13939]">{{ fieldError('contact_phone') }}</p>
                            </div>
                        </div>

                        <div class="grid md:grid-cols-2 gap-5 md:gap-6 mt-5">
                            <div>
                                <label class="text-base md:text-lg text-title dark:text-white leading-none mb-2 sm:mb-3 block">Town / City</label>
                                <input v-model="form.shipping_city" class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300" type="text" placeholder="Enter your city">
                                <p v-if="fieldError('shipping_city')" class="mt-2 text-sm text-[#E13939]">{{ fieldError('shipping_city') }}</p>
                            </div>

                            <div>
                                <label class="text-base md:text-lg text-title dark:text-white leading-none mb-2 sm:mb-3 block">Country</label>
                                <input v-model="form.shipping_country" class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300" type="text" placeholder="Enter your country">
                                <p v-if="fieldError('shipping_country')" class="mt-2 text-sm text-[#E13939]">{{ fieldError('shipping_country') }}</p>
                            </div>

                            <div class="md:col-span-2">
                                <label class="text-base md:text-lg text-title dark:text-white leading-none mb-2 sm:mb-3 block">Address Line</label>
                                <input v-model="form.shipping_address" class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300" type="text" placeholder="Your full address">
                                <p v-if="fieldError('shipping_address')" class="mt-2 text-sm text-[#E13939]">{{ fieldError('shipping_address') }}</p>
                            </div>
                        </div>

                        <div class="mt-5">
                            <label class="text-base md:text-lg text-title dark:text-white leading-none mb-2 sm:mb-3 block">Additional Note</label>
                            <textarea v-model="form.customer_note" class="w-full h-[120px] bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300" name="message" placeholder="Type your message"></textarea>
                            <p v-if="fieldError('customer_note')" class="mt-2 text-sm text-[#E13939]">{{ fieldError('customer_note') }}</p>
                        </div>
                    </form>

                    <div data-aos="fade-up" data-aos-delay="200">
                        <div class="bg-[#FAFAFA] dark:bg-dark-secondary pt-[30px] md:pt-[40px] lg:pt-[50px] px-[30px] md:px-[40px] lg:px-[50px] pb-[30px] border border-[#17243026] border-opacity-15 rounded-xl">
                            <h4 class="font-semibold leading-none text-xl md:text-2xl mb-6 md:mb-10">
                                Product Information
                            </h4>
                            <div class="grid gap-5 mg:gap-6">
                                <div v-for="item in items" :key="item.cart_key" class="flex items-start justify-between gap-5">
                                    <div class="flex items-start gap-3 md:gap-4 lg:gap-6 cart-product flex-wrap">
                                        <div class="w-16 sm:w-[70px] flex-none">
                                            <img :src="item.image || fallbackImage" :alt="item.name">
                                        </div>
                                        <div class="flex-1">
                                            <h6 class="leading-none font-medium">{{ item.category_name || 'Product' }}</h6>
                                            <h5 class="font-semibold leading-none mt-2">
                                                <router-link :to="item.product_id ? `/product-details/${item.product_id}` : '/product-details'">{{ item.name }}</router-link>
                                            </h5>
                                            <p class="mt-2 text-sm text-paragraph dark:text-white-light">Qty: {{ item.quantity }}</p>
                                            <div v-if="item.selected_modifiers.length" class="mt-3 grid gap-2">
                                                <div v-for="modifier in item.selected_modifiers" :key="modifier.modifier_id" class="flex items-center gap-2 flex-wrap text-sm text-paragraph dark:text-white-light">
                                                    <span class="font-medium text-title dark:text-white">{{ modifier.modifier_name }}:</span>
                                                    <div class="flex items-center gap-2 flex-wrap">
                                                        <template v-for="option in modifier.options" :key="`${modifier.modifier_id}-${option.option_id || option.label}`">
                                                            <span
                                                                v-if="modifier.is_color"
                                                                class="inline-flex w-6 h-6 rounded-full border border-[#E3E5E6] dark:border-white/20"
                                                                :title="option.label"
                                                                :style="{ backgroundColor: option.value || '#D4D4D8' }"
                                                            ></span>
                                                            <span v-else class="inline-flex rounded-full bg-white dark:bg-title px-3 py-1 border border-[#E3E5E6] dark:border-white/15">
                                                                {{ option.label }}
                                                            </span>
                                                        </template>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <h6 class="leading-none">{{ item.formattedLineTotal }}</h6>
                                </div>
                            </div>
                            <div class="mt-6 rounded-xl border border-[#17243026] border-opacity-15 bg-white dark:bg-title/20 p-4 md:p-5">
                                <div class="flex items-center justify-between gap-3 flex-wrap">
                                    <h5 class="font-medium leading-none">Promo code</h5>
                                    <button
                                        v-if="appliedPromo"
                                        type="button"
                                        class="text-sm text-primary underline underline-offset-4"
                                        @click="removePromoCode"
                                    >
                                        Remove
                                    </button>
                                </div>
                                <div class="mt-4 flex flex-col sm:flex-row gap-3">
                                    <input
                                        v-model="promoCodeInput"
                                        class="flex-1 h-12 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary px-4 outline-none duration-300"
                                        type="text"
                                        placeholder="Enter promo code"
                                    >
                                    <button
                                        type="button"
                                        class="btn btn-outline min-w-[132px]"
                                        :disabled="applyingPromo"
                                        @click="applyPromoCode"
                                    >
                                        <span>{{ applyingPromo ? 'Applying...' : 'Apply' }}</span>
                                    </button>
                                </div>
                                <p v-if="promoMessage" class="mt-3 text-sm text-[#31A051]">{{ promoMessage }}</p>
                                <p v-if="promoError" class="mt-3 text-sm text-[#E13939]">{{ promoError }}</p>
                                <div v-if="appliedPromo" class="mt-3 text-sm text-paragraph dark:text-white-light">
                                    {{ appliedPromo.code }} applied: {{ appliedPromo.discountLabel }}
                                </div>
                            </div>
                            <div class="mt-6 pt-6 border-t border-bdr-clr dark:border-bdr-clr-drk text-right flex justify-end flex-col w-full ml-auto mr-0">
                                <div class="flex justify-between flex-wrap text-base sm:text-lg text-title dark:text-white font-medium">
                                    <span>Sub Total:</span>
                                    <span>{{ formattedSubtotal }}</span>
                                </div>
                                <div v-if="discountAmount > 0" class="flex justify-between flex-wrap text-base sm:text-lg text-title dark:text-white font-medium mt-3">
                                    <span>Discount:</span>
                                    <span>-{{ formattedDiscountAmount }}</span>
                                </div>
                                <div class="flex justify-between flex-wrap text-base sm:text-lg text-title dark:text-white font-medium mt-3">
                                    <span>Delivery:</span>
                                    <span>{{ formattedDelivery }}</span>
                                </div>
                            </div>
                            <div class="mt-6 pt-6 border-t border-bdr-clr dark:border-bdr-clr-drk">
                                <div class="flex justify-between flex-wrap font-semibold leading-none text-2xl md:text-3xl">
                                    <span>Total:</span>
                                    <span>{{ formattedGrandTotal }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="mt-7 md:mt-12">
                            <h4 class="font-semibold leading-none text-xl md:text-2xl mb-6 md:mb-10">Payment Method</h4>
                            <div class="flex gap-5 sm:gap-8 md:gap-12 flex-wrap">
                                <div>
                                    <label class="flex items-center gap-[10px] categoryies-iteem">
                                        <input v-model="paymentMethod" class="appearance-none hidden" type="radio" value="cash_on_delivery" name="payment-method">
                                        <span class="w-4 h-4 rounded-full border border-title dark:border-white flex items-center justify-center duration-300">
                                            <svg class="duration-300" :class="paymentMethod === 'cash_on_delivery' ? 'opacity-100' : 'opacity-0'" width="8" height="8" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <rect width="10" height="10" rx="5" fill="#BB976D"/>
                                            </svg>
                                        </span>
                                        <span class="sm:text-lg text-title dark:text-white block sm:leading-none transform translate-y-[3px] select-none">Cash On Delivery</span>
                                    </label>
                                    <p class="ml-6 text-[15px] leading-none mt-2">Pay on delivery</p>
                                </div>
                            </div>
                            <div class="mt-6 sm:mt-8 md:mt-10">
                                <label class="flex items-center gap-2 iam-agree">
                                    <input v-model="acceptedTerms" class="appearance-none hidden" type="checkbox" name="categories">
                                    <span class="w-6 h-6 rounded-[5px] border-2 border-title dark:border-white flex items-center justify-center duration-300">
                                        <svg class="duration-300 text-title dark:text-white fill-current" :class="acceptedTerms ? 'opacity-100' : 'opacity-0'" width="15" height="12" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M18.3819 0.742676L6.10461 11.8998L2.25731 8.06381L0.763672 9.55745L6.20645 15.0002L20 2.32686L18.3819 0.742676Z"/>
                                        </svg>
                                    </span>
                                    <span class="text-base sm:text-lg text-title dark:text-white leading-none sm:leading-none select-none inline-block transform translate-y-[3px]">I Agree all terms & Conditions</span>
                                </label>
                                <p v-if="priceNotice" class="mt-4 text-sm text-[#8A5A00] dark:text-[#E8C87A]">{{ priceNotice }}</p>
                                <p v-if="generalError" class="mt-4 text-sm text-[#E13939]">{{ generalError }}</p>
                                <p v-if="successMessage" class="mt-4 text-sm text-[#31A051]">{{ successMessage }}</p>
                            </div>
                            <div class="mt-4 md:mt-6 flex flex-wrap gap-3">
                                <router-link to="/cart" class="btn btn-outline" data-text="Back to Cart"><span>Back to Cart</span></router-link>
                                <button type="button" class="btn btn-theme-solid" data-text="Place Order" :disabled="submitting" @click="placeOrder">
                                    <span>{{ submitting ? 'Placing Order...' : 'Place Order' }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <FooterOne/>

        <ScrollToTop/>

    </div>
</template>

<script setup>
    import { computed, onMounted, reactive, ref, watch } from 'vue';
    import { useRouter } from 'vue-router';
    import NavbarOne from '@/components/navbar/navbar-one.vue';
    import FooterOne from '@/components/footer/footer-one.vue';
    import ScrollToTop from '@/components/scroll-to-top.vue';
    import bg from '@/assets/img/shortcode/breadcumb.jpg'
    import fallbackImage from '@/assets/img/gallery/cart/cart-01.jpg'
    import Aos from 'aos';
    import { formatPrice, storefrontRequest } from '@/lib/storefront-api';
    import { useCart } from '@/composables/use-cart';
    import { useClientSession } from '@/composables/use-client-session';
    import { apiRequest } from '@/lib/client-api';

    const DELIVERY_AMOUNT = 0;

    const router = useRouter();
    const cart = useCart();
    const priceNotice = cart.priceNotice;
    const session = useClientSession();
    const paymentMethod = ref('cash_on_delivery');
    const acceptedTerms = ref(false);
    const submitting = ref(false);
    const generalError = ref('');
    const successMessage = ref('');
    const validationErrors = ref({});
    const promoCodeInput = ref('');
    const appliedPromo = ref(null);
    const promoError = ref('');
    const promoMessage = ref('');
    const applyingPromo = ref(false);
    const savedAddresses = ref([]);
    const selectedAddressId = ref(null);
    const saveAddressForNextTime = ref(true);
    const form = reactive({
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        shipping_address: '',
        shipping_city: '',
        shipping_country: '',
        customer_note: '',
    });

    const items = cart.items;
    const isAuthenticated = computed(() => session.isAuthenticated.value);
    const formattedSubtotal = cart.formattedSubtotal;
    const formattedDelivery = computed(() => formatPrice(DELIVERY_AMOUNT));
    const discountAmount = computed(() => {
        if (!appliedPromo.value) {
            return 0;
        }

        const subtotal = Number(cart.subtotal.value || 0);

        if (appliedPromo.value.discount_type === 'percentage') {
            return Math.min(subtotal, Number(((subtotal * Number(appliedPromo.value.discount_value || 0)) / 100).toFixed(2)));
        }

        return Math.min(subtotal, Number(appliedPromo.value.discount_value || 0));
    });
    const formattedDiscountAmount = computed(() => formatPrice(discountAmount.value));
    const grandTotal = computed(() => Math.max(0, cart.subtotal.value - discountAmount.value) + DELIVERY_AMOUNT);
    const formattedGrandTotal = computed(() => formatPrice(grandTotal.value));

    function fillFromProfile(user) {
        form.contact_name = user?.name || '';
        form.contact_phone = user?.phone || '';
        form.contact_email = user?.email || '';
        form.shipping_address = user?.address_line || '';
        form.shipping_city = user?.city || '';
        form.shipping_country = user?.country || '';
    }

    function fillFromAddress(address) {
        form.contact_name = address?.full_name || '';
        form.contact_phone = address?.phone || '';
        form.contact_email = session.state.user?.email || '';
        form.shipping_address = address?.address_line || '';
        form.shipping_city = address?.city || '';
        form.shipping_country = address?.country || '';
    }

    function selectSavedAddress(address) {
        selectedAddressId.value = address.id;
        fillFromAddress(address);
    }

    function clearSelectedAddress() {
        selectedAddressId.value = null;
        fillFromProfile(session.state.user);
    }

    async function loadSavedAddresses() {
        if (!session.token.value) {
            savedAddresses.value = [];
            return;
        }

        try {
            const response = await apiRequest('/addresses', {
                token: session.token.value,
            });

            savedAddresses.value = response.data || [];

            const preferredAddress = savedAddresses.value.find((address) => address.is_default) || savedAddresses.value[0];

            if (preferredAddress) {
                selectSavedAddress(preferredAddress);
            }
        } catch (error) {
            savedAddresses.value = [];
        }
    }

    async function maybeSaveAddress() {
        if (!isAuthenticated.value || !saveAddressForNextTime.value || !session.token.value) {
            return;
        }

        const payload = {
            full_name: form.contact_name,
            phone: form.contact_phone,
            address_line: form.shipping_address,
            city: form.shipping_city,
            country: form.shipping_country,
            is_default: savedAddresses.value.length === 0 || !selectedAddressId.value,
        };

        try {
            const response = await apiRequest('/addresses', {
                method: 'POST',
                token: session.token.value,
                data: payload,
            });

            const savedAddress = response.data;
            const existingIndex = savedAddresses.value.findIndex((address) => address.id === savedAddress.id);

            if (existingIndex >= 0) {
                savedAddresses.value.splice(existingIndex, 1, savedAddress);
            } else {
                savedAddresses.value.unshift(savedAddress);
            }

            if (savedAddress.is_default) {
                savedAddresses.value = savedAddresses.value.map((address) => ({
                    ...address,
                    is_default: address.id === savedAddress.id,
                }));
            }

            selectedAddressId.value = savedAddress.id;
        } catch (error) {
            // We keep checkout success even if address persistence fails.
        }
    }

    async function applyPromoCode(silent = false) {
        promoError.value = '';

        const code = promoCodeInput.value.trim();

        if (!code) {
            if (!silent) {
                promoError.value = 'Please enter a promo code.';
            }
            appliedPromo.value = null;
            promoMessage.value = '';
            return;
        }

        applyingPromo.value = true;

        try {
            const response = await storefrontRequest('/promo-codes/preview', {
                method: 'POST',
                data: {
                    promo_code: code,
                    subtotal: Number(cart.subtotal.value || 0),
                },
            });

            appliedPromo.value = {
                id: response.data.id,
                code: response.data.code,
                discount_type: response.data.discount_type,
                discount_value: Number(response.data.discount_value || 0),
                discountLabel: response.data.discount_label,
            };
            promoCodeInput.value = response.data.code;
            promoMessage.value = !silent ? (response.message || 'Promo code applied.') : '';
        } catch (error) {
            appliedPromo.value = null;
            promoMessage.value = '';
            if (!silent) {
                promoError.value = error.message || 'Unable to apply this promo code.';
            }
        } finally {
            applyingPromo.value = false;
        }
    }

    function removePromoCode() {
        promoCodeInput.value = '';
        appliedPromo.value = null;
        promoError.value = '';
        promoMessage.value = '';
    }

    function fieldError(field) {
        return validationErrors.value[field]?.[0] || '';
    }

    function orderItemsPayload() {
        return items.value.map((item) => ({
            product_id: item.product_id,
            name: item.name,
            quantity: item.quantity,
            catalog_unit_price: Number(item.base_price || 0),
            unit_price: Number(item.unit_total || item.base_price || 0),
            selected_modifiers: item.selected_modifiers.map((modifier) => ({
                modifier_id: modifier.modifier_id,
                modifier_name: modifier.modifier_name,
                selection_type: modifier.selection_type,
                options: modifier.options.map((option) => ({
                    option_id: option.option_id,
                    label: option.label,
                    price_adjustment: Number(option.price_adjustment || 0),
                })),
            })),
        }));
    }

    async function placeOrder() {
        generalError.value = '';
        successMessage.value = '';
        validationErrors.value = {};

        if (!acceptedTerms.value) {
            generalError.value = 'Please accept the terms and conditions before placing the order.';
            return;
        }

        if (!items.value.length) {
            generalError.value = 'Your cart is empty.';
            return;
        }

        submitting.value = true;

        try {
            const response = await storefrontRequest('/orders/website', {
                method: 'POST',
                data: {
                    ...form,
                    customer_id: session.state.user?.customer_id || session.state.user?.customer?.id || null,
                    delivery_amount: DELIVERY_AMOUNT,
                    currency: 'USD',
                    promo_code: appliedPromo.value?.code || null,
                    source_channel: 'website',
                    items: orderItemsPayload(),
                },
            });

            await maybeSaveAddress();

            const customerMailSent = Boolean(response.meta?.customer_mail_sent);
            const adminMailSent = Boolean(response.meta?.admin_mail_sent);

            successMessage.value = customerMailSent && adminMailSent
                ? (response.message || 'Order placed successfully.')
                : 'Order saved successfully, but one or more emails could not be sent.';
            cart.clearCart();
            router.push({
                path: '/thank-you',
                query: {
                    order: response.data?.number || '',
                    invoice: response.data?.invoice_url || '',
                    customerMail: customerMailSent ? '1' : '0',
                    adminMail: adminMailSent ? '1' : '0',
                },
            });
        } catch (error) {
            validationErrors.value = error.errors || {};

            // Availability and catalog problems come back keyed per line
            // (items.0.quantity, items.1.product_id); those messages name the piece,
            // so they are more useful than the generic failure text.
            const lineMessages = Object.entries(validationErrors.value)
                .filter(([field]) => field.startsWith('items.'))
                .flatMap(([, messages]) => messages);

            generalError.value = lineMessages.length
                ? lineMessages.join(' ')
                : (error.message || 'Unable to place the order right now.');
        } finally {
            submitting.value = false;
        }
    }

    watch(() => session.state.user, (user) => {
        fillFromProfile(user);
    }, { immediate: true });

    watch(() => cart.subtotal.value, () => {
        if (appliedPromo.value && promoCodeInput.value.trim()) {
            applyPromoCode(true);
        }
    });

    onMounted(async () => {
        Aos.init();
        cart.initializeCart();
        await cart.refreshPrices().catch(() => null);
        await session.initializeSession().catch(() => null);
        if (session.state.user) {
            fillFromProfile(session.state.user);
            await loadSavedAddresses();
        }
    });
</script>
